<template>
  <div>
    <div class="card">
      <div class="card-header animated fadeInRightBig">
        <div class="card-title">
          Plantões
        </div>
        <div style="margin-left: auto; display:flex; gap:8px; align-items:center;">
          <div
            v-if="importInBackground"
            class="alert alert-info mb-0"
            style="padding:6px 10px; display:flex; align-items:center; gap:10px;"
          >
            <i class="fas fa-hourglass-half" />
            <div style="font-size:0.9rem;">
              Importação em andamento
            </div>
            <button
              class="btn btn-sm btn-outline-light"
              @click="checkImportResults"
            >
              Ver resultados
            </button>
          </div>
          <button
            class="btn btn-outline-warning"
            style="position:relative;"
            @click="openReleaseRequestsModal"
          >
            <i class="fas fa-inbox" /> Solicitações
            <span
              v-if="pendingReleaseRequests.length"
              style="position:absolute;top:-6px;right:-6px;background:#e67e22;color:#fff;border-radius:50%;min-width:17px;height:17px;font-size:10px;display:flex;align-items:center;justify-content:center;font-weight:bold;padding:0 3px;"
            >
              {{ pendingReleaseRequests.length }}
            </span>
          </button>
          <button
            class="btn btn-secondary"
            :disabled="importInBackground"
            @click="openImportModal"
          >
            <i class="fas fa-file-import" /> Importar
          </button>
          <button
            class="btn btn-primary"
            @click="openShiftModal(null)"
          >
            <i class="fas fa-plus" /> Novo Plantão
          </button>
        </div>
      </div>

      <div class="card-body">
        <div class="row mb-3">
          <div class="col-md-3">
            <label>A partir de</label>
            <input
              v-model="filterFrom"
              type="date"
              class="form-control"
              @change="loadShifts"
            >
          </div>
          <div class="col-md-3">
            <label>Até</label>
            <input
              v-model="filterTo"
              type="date"
              class="form-control"
              @change="loadShifts"
            >
          </div>
          <div class="col-md-4">
            <label>Pesquisar</label>
            <input
              v-model="shiftsSearch"
              type="text"
              class="form-control"
              placeholder="Título, requisito..."
            >
          </div>
        </div>

        <div
          v-if="loading"
          class="text-center py-4"
        >
          <i class="fas fa-spinner fa-spin fa-2x text-primary" />
        </div>

        <div v-else>
          <table
            v-if="filteredShifts.length"
            class="table table-hover table-bordered"
          >
            <thead class="thead-light">
              <tr>
                <th>Título</th>
                <th>Início</th>
                <th>Fim</th>
                <th>Vagas</th>
                <th>Alocados</th>
                <th>Disponíveis</th>
                <th>Requisitos</th>
                <th>Ações</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="shift in paginatedShifts"
                :key="shift.id"
              >
                <td>{{ shift.title }}</td>
                <td>{{ formatDatetime(shift.start_datetime) }}</td>
                <td>{{ formatDatetime(shift.end_datetime) }}</td>
                <td>{{ shift.vacancies }}</td>
                <td>{{ shift.allocated_count }}</td>
                <td>
                  <span :class="shift.available_vacancies > 0 ? 'badge badge-success' : 'badge badge-danger'">
                    {{ shift.available_vacancies }}
                  </span>
                </td>
                <td>
                  <span v-if="shift.requirements && shift.requirements.length">
                    <span
                      v-for="req in shift.requirements"
                      :key="req.id"
                      class="badge badge-secondary mr-1"
                      :title="req.description"
                    >{{ req.name }}</span>
                  </span>
                  <span
                    v-else
                    class="text-muted small"
                  >—</span>
                </td>
                <td>
                  <button
                    class="btn btn-sm btn-info mr-1"
                    title="Ver alocações"
                    aria-label="Ver alocações"
                    style="position:relative;"
                    @click="openAllocationsModal(shift)"
                  >
                    <i class="fas fa-users" />
                    <span
                      v-if="shift.pending_allocations_count > 0"
                      style="position:absolute;top:-6px;right:-6px;background:#e74c3c;color:#fff;border-radius:50%;width:17px;height:17px;font-size:10px;display:flex;align-items:center;justify-content:center;font-weight:bold;line-height:1;"
                    >{{ shift.pending_allocations_count }}</span>
                  </button>
                  <button
                    class="btn btn-sm btn-warning mr-1"
                    title="Editar"
                    aria-label="Editar plantão"
                    @click="openShiftModal(shift)"
                  >
                    <i class="fas fa-edit" />
                  </button>
                  <button
                    class="btn btn-sm btn-danger"
                    title="Excluir"
                    aria-label="Excluir plantão"
                    @click="confirmDelete(shift)"
                  >
                    <i class="fas fa-trash" />
                  </button>
                </td>
              </tr>
            </tbody>
          </table>
          <div
            v-else
            class="text-center text-muted py-4"
          >
            {{ shiftsSearch ? 'Nenhum plantão encontrado para "' + shiftsSearch + '".' : 'Nenhum plantão encontrado.' }}
          </div>

          <!-- Paginação plantões -->
          <div
            v-if="shiftsTotalPages > 1"
            class="d-flex justify-content-between align-items-center mt-3"
            style="font-size:0.88rem;"
          >
            <span class="text-muted">
              {{ (shiftsPage - 1) * shiftsPerPage + 1 }}–{{ Math.min(shiftsPage * shiftsPerPage, filteredShifts.length) }}
              de {{ filteredShifts.length }} plantão(ões)
              <span
                v-if="shiftsSearch && shifts.length !== filteredShifts.length"
                class="ml-1"
              >({{ shifts.length }} total)</span>
            </span>
            <nav>
              <ul class="pagination pagination-sm mb-0">
                <li
                  class="page-item"
                  :class="{ disabled: shiftsPage === 1 }"
                >
                  <button
                    class="page-link"
                    @click="shiftsPage--"
                  >
                    &laquo;
                  </button>
                </li>
                <li
                  v-for="p in shiftsTotalPages"
                  :key="p"
                  class="page-item"
                  :class="{ active: p === shiftsPage }"
                >
                  <button
                    class="page-link"
                    @click="shiftsPage = p"
                  >
                    {{ p }}
                  </button>
                </li>
                <li
                  class="page-item"
                  :class="{ disabled: shiftsPage === shiftsTotalPages }"
                >
                  <button
                    class="page-link"
                    @click="shiftsPage++"
                  >
                    &raquo;
                  </button>
                </li>
              </ul>
            </nav>
          </div>
        </div>
      </div>
    </div>

    <!-- Modal: Solicitações de Liberação Pendentes -->
    <div
      v-if="showReleaseRequestsModal"
      class="modal-backdrop-custom"
      @click.self="showReleaseRequestsModal = false"
    >
      <div
        class="modal-dialog"
        role="document"
        style="max-width:1280px; margin:40px auto; width:96%;"
      >
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">
              <i class="fas fa-inbox mr-2" />Solicitações de Liberação Pendentes
              <span
                v-if="pendingReleaseRequests.length"
                class="badge badge-warning ml-2"
              >{{ pendingReleaseRequests.length }}</span>
            </h5>
            <button
              type="button"
              class="close"
              @click="showReleaseRequestsModal = false"
            >
              <span>&times;</span>
            </button>
          </div>
          <div
            class="modal-body"
            style="padding:16px 20px;"
          >
            <div
              v-if="loadingReleaseRequests"
              class="text-center py-4"
            >
              <i class="fas fa-spinner fa-spin fa-2x text-primary" />
            </div>
            <div
              v-else-if="!pendingReleaseRequests.length"
              class="text-muted text-center py-4"
            >
              <i class="fas fa-check-circle fa-2x text-success mb-2" />
              <p class="mb-0">
                Nenhuma solicitação pendente.
              </p>
            </div>
            <template v-else>
              <div class="mb-3">
                <input
                  v-model="rrSearch"
                  type="text"
                  class="form-control form-control-sm"
                  placeholder="Pesquisar colaborador..."
                >
                <small
                  v-if="rrSearch"
                  class="text-muted"
                >
                  {{ filteredReleaseRequests.length }} de {{ pendingReleaseRequests.length }} solicitações
                </small>
              </div>
              <table class="table table-hover table-bordered table-sm mb-2">
                <thead class="thead-light">
                  <tr>
                    <th>Colaborador</th>
                    <th>Data</th>
                    <th>Tipo</th>
                    <th>Horário original</th>
                    <th>Proposto / Restante</th>
                    <th>Motivo</th>
                    <th>Ações</th>
                  </tr>
                </thead>
                <tbody>
                  <tr
                    v-for="rr in paginatedReleaseRequests"
                    :key="rr.id"
                  >
                    <td>{{ rr.user && rr.user.name }}</td>
                    <td>{{ rr.work_schedule && rr.work_schedule.duty_day ? formatDutyDay(rr.work_schedule.duty_day) : '—' }}</td>
                    <td>
                      <span
                        v-if="!rr.proposed_start_time"
                        class="badge badge-warning"
                      >Completo</span>
                      <span
                        v-else
                        class="badge badge-info"
                      >Parcial</span>
                    </td>
                    <td>
                      <span v-if="rr.work_schedule && rr.work_schedule.shift_start_time">
                        {{ formatShiftTimeRange(rr.work_schedule.duty_day, rr.work_schedule.shift_start_time, rr.work_schedule.shift_end_time) }}
                      </span>
                      <span
                        v-else
                        class="text-muted"
                      >—</span>
                    </td>
                    <td>
                      <span
                        v-if="!rr.proposed_start_time"
                        class="text-muted"
                        style="font-size:0.8rem;"
                      >turno completo</span>
                      <template v-else>
                        <div>{{ formatShiftTimeRange(rr.work_schedule && rr.work_schedule.duty_day, rr.proposed_start_time, rr.proposed_end_time) }}</div>
                        <div
                          v-if="calcRemainingDisplay(rr)"
                          class="text-info"
                          style="font-size:0.78rem;"
                        >
                          ▸ {{ calcRemainingDisplay(rr) }}
                        </div>
                      </template>
                    </td>
                    <td style="max-width:160px;">
                      <span
                        v-if="rr.reason"
                        :title="rr.reason"
                        style="cursor:help;"
                      >
                        {{ rr.reason.length > 40 ? rr.reason.slice(0, 40) + '…' : rr.reason }}
                      </span>
                      <span
                        v-else
                        class="text-muted"
                      >—</span>
                    </td>
                    <td style="white-space:nowrap;">
                      <button
                        class="btn btn-sm btn-success mr-1"
                        title="Aprovar"
                        @click="openApproveReleaseModal(rr)"
                      >
                        <i class="fas fa-check" /> Aprovar
                      </button>
                      <button
                        class="btn btn-sm btn-outline-danger"
                        title="Recusar"
                        @click="openRejectReleaseModal(rr)"
                      >
                        <i class="fas fa-times" /> Recusar
                      </button>
                    </td>
                  </tr>
                </tbody>
              </table>

              <div
                v-if="!filteredReleaseRequests.length"
                class="text-center text-muted py-3"
              >
                Nenhuma solicitação encontrada para "{{ rrSearch }}".
              </div>

              <!-- Paginação solicitações -->
              <div
                v-if="rrTotalPages > 1"
                class="d-flex justify-content-between align-items-center"
                style="font-size:0.88rem;"
              >
                <span class="text-muted">
                  {{ (rrPage - 1) * rrPerPage + 1 }}–{{ Math.min(rrPage * rrPerPage, filteredReleaseRequests.length) }}
                  de {{ filteredReleaseRequests.length }} solicitações
                </span>
                <nav>
                  <ul class="pagination pagination-sm mb-0">
                    <li
                      class="page-item"
                      :class="{ disabled: rrPage === 1 }"
                    >
                      <button
                        class="page-link"
                        @click="rrPage--"
                      >
                        &laquo;
                      </button>
                    </li>
                    <li
                      v-for="p in rrTotalPages"
                      :key="p"
                      class="page-item"
                      :class="{ active: p === rrPage }"
                    >
                      <button
                        class="page-link"
                        @click="rrPage = p"
                      >
                        {{ p }}
                      </button>
                    </li>
                    <li
                      class="page-item"
                      :class="{ disabled: rrPage === rrTotalPages }"
                    >
                      <button
                        class="page-link"
                        @click="rrPage++"
                      >
                        &raquo;
                      </button>
                    </li>
                  </ul>
                </nav>
              </div>
            </template>
          </div>
          <div class="modal-footer">
            <button
              type="button"
              class="btn btn-secondary"
              @click="showReleaseRequestsModal = false"
            >
              Fechar
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Modal: Aprovar Solicitação de Liberação -->
    <div
      v-if="showApproveReleaseModal && approvingReleaseReq"
      class="modal-backdrop-custom"
      @click.self="showApproveReleaseModal = false"
    >
      <div
        class="modal-dialog"
        role="document"
        style="max-width:880px; margin:60px auto; width:95%;"
      >
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">
              <i class="fas fa-check mr-2" />Aprovar liberação
            </h5>
            <button
              type="button"
              class="close"
              @click="showApproveReleaseModal = false"
            >
              <span>&times;</span>
            </button>
          </div>
          <div
            class="modal-body"
            style="overflow-y:auto; max-height:calc(100vh - 200px);"
          >
            <div
              class="mb-3 p-2 bg-light rounded"
              style="font-size:0.88rem;"
            >
              <strong>{{ approvingReleaseReq.user && approvingReleaseReq.user.name }}</strong>
              <span class="text-muted ml-2">
                {{ approvingReleaseReq.work_schedule && approvingReleaseReq.work_schedule.duty_day
                  ? formatDutyDay(approvingReleaseReq.work_schedule.duty_day)
                  : '' }}
              </span>
            </div>
            <!-- Full release -->
            <template v-if="!approvingReleaseReq.proposed_start_time">
              <div
                class="alert alert-warning mb-2"
                style="font-size:0.85rem; padding:8px 12px;"
              >
                <i class="fas fa-unlock mr-1" />
                <strong>Liberação completa</strong> — o turno inteiro será liberado.
              </div>
              <div
                class="row mb-2"
                style="font-size:0.88rem;"
              >
                <div class="col-12">
                  <div class="text-muted">
                    Horário original (virará plantão)
                  </div>
                  <strong v-if="approvingReleaseReq.work_schedule && approvingReleaseReq.work_schedule.shift_start_time">
                    {{ formatShiftTimeRange(approvingReleaseReq.work_schedule.duty_day, approvingReleaseReq.work_schedule.shift_start_time, approvingReleaseReq.work_schedule.shift_end_time) }}
                  </strong>
                  <span
                    v-else
                    class="text-muted"
                  >—</span>
                </div>
              </div>
            </template>

            <!-- Partial release -->
            <template v-else>
              <div
                class="row mb-2"
                style="font-size:0.88rem;"
              >
                <div class="col-6">
                  <div class="text-muted">
                    Horário original
                  </div>
                  <strong v-if="approvingReleaseReq.work_schedule && approvingReleaseReq.work_schedule.shift_start_time">
                    {{ formatShiftTimeRange(approvingReleaseReq.work_schedule.duty_day, approvingReleaseReq.work_schedule.shift_start_time, approvingReleaseReq.work_schedule.shift_end_time) }}
                  </strong>
                  <span
                    v-else
                    class="text-muted"
                  >—</span>
                </div>
                <div class="col-6">
                  <div class="text-muted">
                    Novo horário (proposto)
                  </div>
                  <strong>{{ approvingReleaseReq.proposed_start_time.slice(0,5) }} – {{ approvingReleaseReq.proposed_end_time.slice(0,5) }}</strong>
                </div>
              </div>
              <div
                v-if="calcRemainingDisplay(approvingReleaseReq)"
                class="alert alert-info mb-2"
                style="font-size:0.85rem; padding:8px 12px;"
              >
                <i class="fas fa-info-circle" />
                Plantão a criar: <strong>{{ calcRemainingDisplay(approvingReleaseReq) }}</strong>
              </div>
            </template>
            <div
              v-if="approvingReleaseReq.reason"
              class="alert alert-secondary"
              style="font-size:0.85rem; padding:8px 12px;"
            >
              <strong>Motivo do colaborador:</strong> {{ approvingReleaseReq.reason }}
            </div>

            <!-- Toggle: criar plantão opcional (liberação completa ou parcial com horário restante) -->
            <div
              v-if="!approvingReleaseReq.proposed_start_time || calcRemainingDisplay(approvingReleaseReq)"
              class="custom-control custom-switch mt-3 mb-2"
            >
              <input
                id="approve-create-plantao"
                v-model="approveReleaseForm.create_plantao"
                type="checkbox"
                class="custom-control-input"
              >
              <label
                class="custom-control-label"
                for="approve-create-plantao"
                style="font-size:0.9rem;"
              >
                Criar plantão
              </label>
            </div>

            <template v-if="approveReleaseForm.create_plantao">
              <div class="form-row mt-2">
                <div class="form-group col-md-8">
                  <label><strong>Título do plantão</strong></label>
                  <input
                    v-model="approveReleaseForm.plantao_title"
                    type="text"
                    class="form-control"
                    placeholder="Ex: Plantão tarde"
                    maxlength="255"
                  >
                </div>
                <div class="form-group col-md-4">
                  <label><strong>Vagas</strong></label>
                  <input
                    v-model.number="approveReleaseForm.plantao_vacancies"
                    type="number"
                    class="form-control"
                    min="1"
                    max="99"
                  >
                </div>
              </div>
              <div
                v-if="availableRequirements.length"
                class="form-group mb-0"
              >
                <label style="font-size:0.85rem;"><strong>Requisitos</strong> <span class="text-muted">(opcional)</span></label>
                <div style="max-height:80px; overflow-y:auto; background:#f8f9fa; border:1px solid #dee2e6; border-radius:4px; padding:6px 8px;">
                  <div
                    v-for="req in availableRequirements"
                    :key="req.id"
                    class="custom-control custom-checkbox"
                    style="line-height:1.4;"
                  >
                    <input
                      :id="`approve-rel-req-${req.id}`"
                      v-model="approveReleaseForm.plantao_requirement_ids"
                      type="checkbox"
                      class="custom-control-input"
                      :value="req.id"
                    >
                    <label
                      class="custom-control-label"
                      :for="`approve-rel-req-${req.id}`"
                      style="font-size:0.82rem;"
                    >{{ req.name }}</label>
                  </div>
                </div>
                <small
                  class="text-muted"
                  style="font-size:0.78rem;"
                >Sem requisitos = visível para todos</small>
              </div>
            </template>
          </div>
          <div class="modal-footer">
            <button
              type="button"
              class="btn btn-secondary"
              @click="showApproveReleaseModal = false"
            >
              Cancelar
            </button>
            <button
              type="button"
              class="btn btn-success"
              :disabled="submittingApproveRelease"
              @click="submitApproveRelease"
            >
              <span v-if="submittingApproveRelease"><i class="fa fa-spinner fa-spin" /> Aprovando...</span>
              <span v-else><i class="fas fa-check" /> Aprovar</span>
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Modal: Recusar Solicitação de Liberação -->
    <div
      v-if="showRejectReleaseModal && rejectingReleaseReq"
      class="modal-backdrop-custom"
      @click.self="showRejectReleaseModal = false"
    >
      <div
        class="modal-dialog"
        role="document"
        style="max-width:680px; margin:60px auto; width:95%;"
      >
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">
              <i class="fas fa-times mr-2" />Recusar solicitação
            </h5>
            <button
              type="button"
              class="close"
              @click="showRejectReleaseModal = false"
            >
              <span>&times;</span>
            </button>
          </div>
          <div class="modal-body">
            <div
              class="mb-3 p-2 bg-light rounded"
              style="font-size:0.88rem;"
            >
              <strong>{{ rejectingReleaseReq.user && rejectingReleaseReq.user.name }}</strong>
              — Proposto: {{ rejectingReleaseReq.proposed_start_time ? rejectingReleaseReq.proposed_start_time.slice(0,5) : '?' }}
              – {{ rejectingReleaseReq.proposed_end_time ? rejectingReleaseReq.proposed_end_time.slice(0,5) : '?' }}
            </div>
            <div
              v-if="rejectingReleaseReq.reason"
              class="mb-3 text-muted"
              style="font-size:0.85rem;"
            >
              <strong>Motivo do colaborador:</strong> {{ rejectingReleaseReq.reason }}
            </div>
            <div class="form-group mb-0">
              <label><strong>Justificativa da recusa</strong> <span class="text-muted">(opcional)</span></label>
              <textarea
                v-model="rejectReleaseReason"
                class="form-control"
                rows="3"
                placeholder="Ex: Necessidade de cobertura no turno completo..."
                maxlength="500"
              />
              <small class="text-muted">O colaborador verá esta justificativa.</small>
            </div>
          </div>
          <div class="modal-footer">
            <button
              type="button"
              class="btn btn-secondary"
              @click="showRejectReleaseModal = false"
            >
              Cancelar
            </button>
            <button
              type="button"
              class="btn btn-danger"
              :disabled="submittingRejectRelease"
              @click="submitRejectRelease"
            >
              <span v-if="submittingRejectRelease"><i class="fa fa-spinner fa-spin" /> Recusando...</span>
              <span v-else><i class="fas fa-times" /> Recusar</span>
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Modal: Criar/Editar Plantão -->
    <div
      v-if="showShiftModal"
      class="modal-backdrop-custom"
      @click.self="closeShiftModal"
    >
      <div
        class="modal-dialog"
        role="document"
        style="max-width:640px; margin:60px auto; width:95%;"
      >
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">
              {{ editingShift ? 'Editar Plantão' : 'Novo Plantão' }}
            </h5>
            <button
              type="button"
              class="close"
              @click="closeShiftModal"
            >
              <span>&times;</span>
            </button>
          </div>
          <div class="modal-body">
            <div class="form-group">
              <label>Título *</label>
              <input
                v-model="shiftForm.title"
                type="text"
                class="form-control"
                placeholder="Ex: Plantão Noturno"
              >
            </div>
            <div class="form-group">
              <label>Data e Hora de Início *</label>
              <input
                v-model="shiftForm.start_datetime"
                type="datetime-local"
                class="form-control"
                :min="editingShift ? null : nowLocalDatetime"
              >
            </div>
            <div class="form-group">
              <label>Data e Hora de Fim *</label>
              <input
                v-model="shiftForm.end_datetime"
                type="datetime-local"
                class="form-control"
                :min="editingShift ? null : (shiftForm.start_datetime || nowLocalDatetime)"
              >
            </div>
            <div class="form-group">
              <label>Vagas *</label>
              <input
                v-model.number="shiftForm.vacancies"
                type="number"
                class="form-control"
                :min="minVacancies"
              >
              <small
                v-if="editingShift && editingShift.allocated_count > 0"
                class="text-muted"
              >
                {{ editingShift.allocated_count }} vaga(s) ocupada(s) — não é possível reduzir abaixo desse total.
              </small>
            </div>
            <div
              v-if="availableRequirements.length"
              class="form-group"
            >
              <label>Requisitos <small class="text-muted">(apenas colaboradores com estes requisitos podem solicitar)</small></label>
              <div style="max-height: 140px; overflow-y: auto; border: 1px solid #ced4da; border-radius: 4px; padding: 8px;">
                <div
                  v-for="req in availableRequirements"
                  :key="req.id"
                  class="form-check"
                >
                  <input
                    :id="'req_' + req.id"
                    v-model="shiftForm.requirement_ids"
                    type="checkbox"
                    class="form-check-input"
                    :value="req.id"
                  >
                  <label
                    class="form-check-label"
                    :for="'req_' + req.id"
                  >
                    {{ req.name }}
                    <small
                      v-if="req.description"
                      class="text-muted"
                    >— {{ req.description }}</small>
                  </label>
                </div>
              </div>
              <small class="text-muted">Sem requisitos selecionados = visível para todos.</small>
            </div>
          </div>
          <div class="modal-footer">
            <button
              class="btn btn-secondary"
              @click="closeShiftModal"
            >
              Cancelar
            </button>
            <button
              class="btn btn-primary"
              :disabled="savingShift"
              @click="saveShift"
            >
              <i
                v-if="savingShift"
                class="fas fa-spinner fa-spin mr-1"
              />
              {{ editingShift ? 'Salvar' : 'Criar' }}
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Modal: Alocações do Plantão -->
    <div
      v-if="showAllocationsModal"
      class="modal-backdrop-custom"
      @click.self="closeAllocationsModal"
    >
      <div
        class="modal-dialog"
        role="document"
        style="max-width:1040px; margin:60px auto; width:96%;"
      >
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">
              Alocações — {{ selectedShift ? selectedShift.title : '' }}
            </h5>
            <button
              type="button"
              class="close"
              @click="closeAllocationsModal"
            >
              <span>&times;</span>
            </button>
          </div>
          <div class="modal-body">
            <div
              v-if="loadingAllocations"
              class="text-center py-3"
            >
              <i class="fas fa-spinner fa-spin fa-2x text-primary" />
            </div>
            <div v-else>

              <!-- Adicionar colaborador diretamente -->
              <div class="card bg-light mb-3">
                <div class="card-body py-2 px-3">
                  <h6 class="mb-2">
                    <i class="fas fa-user-plus mr-1" /> Adicionar colaborador
                  </h6>
                  <div
                    class="d-flex"
                    style="gap: 8px; align-items: flex-end;"
                  >
                    <div class="flex-grow-1">
                      <v-select
                        v-model="addUserSelected"
                        :options="allocatableEmployees"
                        label="name"
                        :reduce="emp => emp.id"
                        placeholder="Buscar colaborador..."
                        :clearable="true"
                      />
                    </div>
                    <button
                      class="btn btn-sm btn-primary"
                      :disabled="!addUserSelected || addingUser"
                      style="white-space: nowrap;"
                      @click="adminAddUser"
                    >
                      <i
                        v-if="addingUser"
                        class="fas fa-spinner fa-spin mr-1"
                      />
                      Adicionar
                    </button>
                  </div>
                  <small class="text-muted">
                    Vagas disponíveis: {{ selectedShift ? selectedShift.available_vacancies : 0 }}
                  </small>
                </div>
              </div>

              <div v-if="pendingAllocations.length">
                <div class="d-flex align-items-center justify-content-between mb-2">
                  <h6 class="text-warning mb-0">
                    <i class="fas fa-clock mr-1" /> Aguardando aprovação ({{ pendingAllocations.length }})
                  </h6>
                  <div
                    v-if="selectedAllocIds.length > 0"
                    style="display:flex; gap:6px;"
                  >
                    <button
                      class="btn btn-sm btn-success"
                      :disabled="bulkApproving"
                      @click="bulkApprove"
                    >
                      <i
                        v-if="bulkApproving"
                        class="fas fa-spinner fa-spin mr-1"
                      />
                      <i
                        v-else
                        class="fas fa-check mr-1"
                      />
                      Aprovar ({{ selectedAllocIds.length }})
                    </button>
                    <button
                      class="btn btn-sm btn-danger"
                      :disabled="bulkRejecting"
                      @click="openBulkRejectModal"
                    >
                      <i class="fas fa-times mr-1" />
                      Rejeitar ({{ selectedAllocIds.length }})
                    </button>
                  </div>
                </div>
                <input
                  v-model="pendingSearch"
                  type="text"
                  class="form-control form-control-sm mb-2"
                  placeholder="Pesquisar colaborador..."
                >
                <table class="table table-sm table-bordered mb-3">
                  <thead>
                    <tr>
                      <th style="width:32px;">
                        <input
                          type="checkbox"
                          :checked="allPendingSelected"
                          @change="toggleSelectAll"
                        >
                      </th>
                      <th>Colaborador</th>
                      <th>Solicitado em</th>
                      <th>Ações</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-if="!filteredPendingAllocations.length">
                      <td
                        colspan="4"
                        class="text-center text-muted"
                      >
                        Nenhum resultado.
                      </td>
                    </tr>
                    <tr
                      v-for="alloc in filteredPendingAllocations"
                      :key="alloc.id"
                    >
                      <td>
                        <input
                          v-model="selectedAllocIds"
                          type="checkbox"
                          :value="alloc.id"
                        >
                      </td>
                      <td>{{ alloc.user ? alloc.user.name : '—' }}</td>
                      <td>{{ formatDatetime(alloc.created_at) }}</td>
                      <td>
                        <button
                          class="btn btn-sm btn-success mr-1"
                          @click="approveAllocation(alloc)"
                        >
                          <i class="fas fa-check" />
                        </button>
                        <button
                          class="btn btn-sm btn-danger"
                          @click="openRejectModal(alloc)"
                        >
                          <i class="fas fa-times" />
                        </button>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <div v-if="approvedAllocations.length">
                <h6 class="text-success">
                  <i class="fas fa-check-circle mr-1" /> Alocados ({{ approvedAllocations.length }})
                </h6>
                <table class="table table-sm table-bordered">
                  <thead><tr><th>Colaborador</th><th>Aprovado em</th><th>Ações</th></tr></thead>
                  <tbody>
                    <tr
                      v-for="alloc in approvedAllocations"
                      :key="alloc.id"
                    >
                      <td>{{ alloc.user ? alloc.user.name : '—' }}</td>
                      <td>{{ alloc.approved_at ? formatDatetime(alloc.approved_at) : '—' }}</td>
                      <td>
                        <button
                          class="btn btn-sm btn-outline-danger"
                          @click="removeAllocation(alloc)"
                        >
                          <i class="fas fa-user-minus" /> Remover
                        </button>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <div
                v-if="!pendingAllocations.length && !approvedAllocations.length"
                class="text-center text-muted py-3"
              >
                Nenhuma alocação para este plantão.
              </div>
            </div>
          </div>
          <div class="modal-footer">
            <button
              class="btn btn-secondary"
              @click="closeAllocationsModal"
            >
              Fechar
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Modal: Motivo da Rejeição -->
    <div
      v-if="showRejectModal"
      class="modal-backdrop-custom"
      @click.self="closeRejectModal"
    >
      <div
        class="modal-dialog"
        role="document"
        style="max-width:480px; margin:100px auto; width:95%;"
      >
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">
              Rejeitar Solicitação
            </h5>
            <button
              type="button"
              class="close"
              @click="closeRejectModal"
            >
              <span>&times;</span>
            </button>
          </div>
          <div class="modal-body">
            <div class="form-group">
              <label>Motivo da rejeição *</label>
              <textarea
                v-model="rejectReason"
                class="form-control"
                rows="3"
                placeholder="Informe o motivo..."
              />
            </div>
          </div>
          <div class="modal-footer">
            <button
              class="btn btn-secondary"
              @click="closeRejectModal"
            >
              Cancelar
            </button>
            <button
              class="btn btn-danger"
              :disabled="!rejectReason.trim() || rejectingAllocation"
              @click="confirmReject"
            >
              <i
                v-if="rejectingAllocation"
                class="fas fa-spinner fa-spin mr-1"
              />
              Rejeitar
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Modal: Rejeição em massa -->
    <div
      v-if="showBulkRejectModal"
      class="modal-backdrop-custom"
      @click.self="closeBulkRejectModal"
    >
      <div
        class="modal-dialog"
        role="document"
        style="max-width:480px; margin:100px auto; width:95%;"
      >
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">
              Rejeitar {{ selectedAllocIds.length }} solicitação(ões)
            </h5>
            <button
              type="button"
              class="close"
              @click="closeBulkRejectModal"
            >
              <span>&times;</span>
            </button>
          </div>
          <div class="modal-body">
            <div class="form-group">
              <label>Motivo da rejeição *</label>
              <textarea
                v-model="bulkRejectReason"
                class="form-control"
                rows="3"
                placeholder="Informe o motivo..."
              />
            </div>
          </div>
          <div class="modal-footer">
            <button
              class="btn btn-secondary"
              @click="closeBulkRejectModal"
            >
              Cancelar
            </button>
            <button
              class="btn btn-danger"
              :disabled="!bulkRejectReason.trim() || bulkRejecting"
              @click="bulkReject"
            >
              <i
                v-if="bulkRejecting"
                class="fas fa-spinner fa-spin mr-1"
              />
              Rejeitar todos
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Modal: Importação -->
    <div
      v-if="showImportModal"
      class="modal-backdrop-custom"
      @click.self="closeImportModal"
    >
      <div
        class="modal-dialog"
        role="document"
        style="max-width:600px; margin:60px auto; width:95%;"
      >
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">
              Importar Plantões
            </h5>
            <button
              type="button"
              class="close"
              @click="closeImportModal"
            >
              <span>&times;</span>
            </button>
          </div>
          <div class="modal-body">
            <p class="text-muted small">
              Faça upload de uma planilha Excel com as colunas:
              <strong>TITULO, DATA_INICIO, HORA_INICIO, HORA_FIM, VAGAS, REQUISITOS</strong>.
              A coluna <strong>REQUISITOS</strong> é opcional — separe múltiplos por ponto e vírgula (ex: <em>CTI;Enfermagem</em>).
            </p>
            <div class="mb-3">
              <button
                class="btn btn-outline-secondary btn-sm"
                @click="downloadTemplate"
              >
                <i class="fas fa-download mr-1" /> Baixar template
              </button>
            </div>
            <div class="form-group">
              <label>Arquivo (.xlsx, .xls, .csv)</label>
              <input
                ref="importFile"
                type="file"
                class="form-control-file"
                accept=".xlsx,.xls,.csv"
                @change="onFileChange"
              >
            </div>
            <div
              v-if="importStatus"
              class="mt-2"
            >
              <div
                v-if="importProgressPercent > 0"
                style="background:#e9ecef; border-radius:4px; height:8px; overflow:hidden;"
              >
                <div :style="{width: importProgressPercent + '%', background:'#007bff', height:'100%'}" />
              </div>
              <div class="text-muted small mt-1">
                {{ importStatus }} <span v-if="importProgressPercent > 0">— {{ importProgressPercent }}%</span>
              </div>
            </div>
            <div
              v-if="importResults"
              class="mt-3"
            >
              <div
                class="alert"
                :class="importResults.errors > 0 ? 'alert-warning' : 'alert-success'"
              >
                <strong>Resultado:</strong>
                {{ importResults.success }} criado(s),
                {{ importResults.errors }} erro(s)
              </div>
              <div
                v-if="importResults.errorList && importResults.errorList.length"
                style="max-height:200px; overflow-y:auto; border:1px solid #ffc107; border-radius:4px;"
              >
                <table class="table table-sm mb-0">
                  <thead class="thead-light">
                    <tr>
                      <th style="width:60px;">
                        Linha
                      </th>
                      <th>Erro</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr
                      v-for="(err, i) in importResults.errorList"
                      :key="i"
                    >
                      <td class="text-muted">
                        {{ err.line }}
                      </td>
                      <td class="text-danger">
                        {{ err.error }}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
          <div class="modal-footer">
            <button
              class="btn btn-secondary"
              @click="closeImportModal"
            >
              Fechar
            </button>
            <button
              class="btn btn-primary"
              :disabled="!importFile || importInProgress"
              @click="startImport"
            >
              <i
                v-if="importInProgress"
                class="fas fa-spinner fa-spin mr-1"
              />
              Importar
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios'

export default {
  name: 'DutyShiftCrud',
  data() {
    return {
      shifts: [],
      loading: false,
      filterFrom: '',
      filterTo: '',

      showShiftModal: false,
      editingShift: null,
      savingShift: false,
      shiftForm: {
        title: '', start_datetime: '', end_datetime: '', vacancies: 1, requirement_ids: [],
      },

      availableRequirements: [],

      showAllocationsModal: false,
      selectedShift: null,
      allocations: [],
      loadingAllocations: false,

      addUserSelected: '',
      addingUser: false,
      allEmployees: [],

      selectedAllocIds: [],
      bulkRejecting: false,
      bulkApproving: false,
      showBulkRejectModal: false,
      bulkRejectReason: '',
      pendingSearch: '',

      showRejectModal: false,
      rejectingAllocation: false,
      rejectReason: '',
      rejectTargetAllocation: null,

      showImportModal: false,
      importFile: null,
      importInProgress: false,
      importInBackground: false,
      importJobId: null,
      importStatus: '',
      importProgressPercent: 0,
      importResults: null,
      importPollingInterval: null,

      shiftsPage: 1,
      shiftsPerPage: 10,
      shiftsSearch: '',

      showReleaseRequestsModal: false,
      pendingReleaseRequests: [],
      loadingReleaseRequests: false,
      rrPage: 1,
      rrPerPage: 10,
      rrSearch: '',

      showApproveReleaseModal: false,
      approvingReleaseReq: null,
      approveReleaseForm: { plantao_title: '', plantao_vacancies: 1, plantao_requirement_ids: [] },
      submittingApproveRelease: false,

      showRejectReleaseModal: false,
      rejectingReleaseReq: null,
      rejectReleaseReason: '',
      submittingRejectRelease: false,
    }
  },
  computed: {
    api() {
      return this.$store.getters.api
    },
    nowLocalDatetime() {
      const d = new Date()
      d.setSeconds(0, 0)
      const tzOffset = d.getTimezoneOffset() * 60000
      return new Date(d.getTime() - tzOffset).toISOString().slice(0, 16)
    },
    minVacancies() {
      if (this.editingShift && this.editingShift.allocated_count > 0) {
        return this.editingShift.allocated_count
      }
      return 1
    },
    filteredShifts() {
      if (!this.shiftsSearch.trim()) return this.shifts
      const q = this.shiftsSearch.toLowerCase()
      return this.shifts.filter(s => (s.title && s.title.toLowerCase().includes(q))
        || (s.requirements && s.requirements.some(r => r.name.toLowerCase().includes(q))))
    },
    paginatedShifts() {
      const start = (this.shiftsPage - 1) * this.shiftsPerPage
      return this.filteredShifts.slice(start, start + this.shiftsPerPage)
    },
    shiftsTotalPages() {
      return Math.ceil(this.filteredShifts.length / this.shiftsPerPage) || 1
    },
    filteredReleaseRequests() {
      if (!this.rrSearch.trim()) return this.pendingReleaseRequests
      const q = this.rrSearch.toLowerCase()
      return this.pendingReleaseRequests.filter(rr => rr.user && rr.user.name.toLowerCase().includes(q))
    },
    paginatedReleaseRequests() {
      const start = (this.rrPage - 1) * this.rrPerPage
      return this.filteredReleaseRequests.slice(start, start + this.rrPerPage)
    },
    rrTotalPages() {
      return Math.ceil(this.filteredReleaseRequests.length / this.rrPerPage) || 1
    },
    pendingAllocations() {
      return this.allocations.filter(a => a.status === 'pending')
    },
    approvedAllocations() {
      return this.allocations.filter(a => a.status === 'approved')
    },
    allocatableEmployees() {
      const allocatedIds = new Set(this.allocations.map(a => a.user_id))
      return this.allEmployees.filter(e => !allocatedIds.has(e.id))
    },
    filteredPendingAllocations() {
      if (!this.pendingSearch.trim()) return this.pendingAllocations
      const q = this.pendingSearch.toLowerCase()
      return this.pendingAllocations.filter(a => a.user && a.user.name.toLowerCase().includes(q))
    },
    allPendingSelected() {
      return this.filteredPendingAllocations.length > 0
        && this.filteredPendingAllocations.every(a => this.selectedAllocIds.includes(a.id))
    },
  },
  watch: {
    shiftsSearch() { this.shiftsPage = 1 },
    rrSearch() { this.rrPage = 1 },
  },
  mounted() {
    this.loadRequirements()
    this.loadShifts()
    this.loadPendingReleaseRequests()
    this.restoreImportState()
  },
  beforeDestroy() {
    this.stopPolling()
  },
  methods: {
    async loadShifts() {
      this.loading = true
      try {
        const params = {}
        if (this.filterFrom) params.from = this.filterFrom
        if (this.filterTo) params.to = `${this.filterTo}T23:59:59`

        const res = await axios.get(`${this.api}duty_shifts`, { params })
        this.shifts = res.data.data || []
        this.shiftsPage = 1
      } catch (e) {
        this.notify('Erro ao carregar plantões.', 'error')
      } finally {
        this.loading = false
      }
    },

    async loadRequirements() {
      try {
        const res = await axios.get(`${this.api}requirements`)
        this.availableRequirements = res.data.data || res.data || []
      } catch (e) {
        this.notify('Erro ao carregar requisitos.', 'error')
      }
    },

    openShiftModal(shift) {
      this.editingShift = shift
      if (shift) {
        this.shiftForm = {
          title: shift.title,
          start_datetime: this.toInputDatetime(shift.start_datetime),
          end_datetime: this.toInputDatetime(shift.end_datetime),
          vacancies: shift.vacancies,
          requirement_ids: (shift.requirements || []).map(r => r.id),
        }
      } else {
        this.shiftForm = {
          title: '', start_datetime: '', end_datetime: '', vacancies: 1, requirement_ids: [],
        }
      }
      this.showShiftModal = true
    },

    closeShiftModal() {
      this.showShiftModal = false
      this.editingShift = null
    },

    async saveShift() {
      if (!this.shiftForm.title || !this.shiftForm.start_datetime || !this.shiftForm.end_datetime || !this.shiftForm.vacancies) {
        this.notify('Preencha todos os campos obrigatórios.', 'warning')
        return
      }
      if (new Date(this.shiftForm.end_datetime) <= new Date(this.shiftForm.start_datetime)) {
        this.notify('A data de fim deve ser posterior à data de início.', 'warning')
        return
      }
      if (!this.editingShift && new Date(this.shiftForm.start_datetime) < new Date(this.nowLocalDatetime)) {
        this.notify('A data de início não pode ser retroativa.', 'warning')
        return
      }
      if (this.editingShift && this.editingShift.allocated_count > 0
          && Number(this.shiftForm.vacancies) < this.editingShift.allocated_count) {
        this.notify(`Não é possível reduzir as vagas para menos que a ocupação atual (${this.editingShift.allocated_count}).`, 'warning')
        return
      }
      if (this.editingShift) {
        const totalAllocated = (this.editingShift.allocated_count || 0) + (this.editingShift.pending_allocations_count || 0)
        const datesChanged = this.shiftForm.start_datetime !== this.toInputDatetime(this.editingShift.start_datetime)
          || this.shiftForm.end_datetime !== this.toInputDatetime(this.editingShift.end_datetime)
        if (totalAllocated > 0 && datesChanged) {
          this.notify('Não é possível alterar a data/hora de um plantão que já possui colaboradores alocados.', 'warning')
          return
        }
      }
      this.savingShift = true
      try {
        const payload = {
          ...this.shiftForm,
          department_id: null,
          requirement_ids: this.shiftForm.requirement_ids || [],
        }
        if (this.editingShift) {
          await axios.put(`${this.api}duty_shifts/${this.editingShift.id}`, payload)
          this.notify('Plantão atualizado com sucesso.', 'success')
        } else {
          await axios.post(`${this.api}duty_shifts`, payload)
          this.notify('Plantão criado com sucesso.', 'success')
          this.filterFrom = ''
          this.filterTo = ''
        }
        this.closeShiftModal()
        this.loadShifts()
      } catch (e) {
        this.notify(this.parseApiError(e, 'Erro ao salvar plantão.'), 'error')
      } finally {
        this.savingShift = false
      }
    },

    parseApiError(e, fallback) {
      const data = e && e.response && e.response.data
      if (!data) return fallback
      if (typeof data.message === 'string' && data.message) return data.message
      if (typeof data === 'object') {
        for (const key of Object.keys(data)) {
          const value = data[key]
          if (Array.isArray(value) && value.length && typeof value[0] === 'string') return value[0]
          if (typeof value === 'string' && value) return value
        }
      }
      return fallback
    },

    async confirmDelete(shift) {
      const allocatedCount = shift.allocated_count || 0
      const text = allocatedCount > 0
        ? `Este plantão possui ${allocatedCount} colaborador(es) alocado(s). As escalas vinculadas também serão removidas. Deseja continuar?`
        : `Deseja remover o plantão "${shift.title}"?`

      const result = await swal({
        title: 'Remover plantão',
        text,
        icon: 'warning',
        buttons: { cancel: 'Cancelar', confirm: { text: 'Remover', value: true, className: 'btn-danger' } },
        dangerMode: true,
      })
      if (!result) return
      try {
        await axios.delete(`${this.api}duty_shifts/${shift.id}`)
        this.notify('Plantão removido.', 'success')
        this.loadShifts()
      } catch (e) {
        this.notify('Erro ao remover plantão.', 'error')
      }
    },

    async openAllocationsModal(shift) {
      this.selectedShift = shift
      this.showAllocationsModal = true
      this.loadingAllocations = true
      this.addUserSelected = ''
      try {
        const [allocRes, empRes] = await Promise.all([
          axios.get(`${this.api}duty_shifts/${shift.id}/allocations`),
          axios.get(`${this.api}duty_shifts/${shift.id}/allocatable-users`),
        ])
        this.allocations = allocRes.data.data || []
        this.allEmployees = (empRes.data.data || empRes.data || []).filter(u => u.name)
      } catch (e) {
        this.notify('Erro ao carregar alocações.', 'error')
      } finally {
        this.loadingAllocations = false
      }
    },

    closeAllocationsModal() {
      this.showAllocationsModal = false
      this.selectedShift = null
      this.allocations = []
      this.selectedAllocIds = []
      this.pendingSearch = ''
    },

    async approveAllocation(alloc) {
      try {
        await axios.post(`${this.api}duty_shift_allocations/${alloc.id}/approve`)
        this.notify('Solicitação aprovada.', 'success')
        this.openAllocationsModal(this.selectedShift)
        this.loadShifts()
      } catch (e) {
        const msg = (e.response && e.response.data && e.response.data.message) || 'Erro ao aprovar.'
        this.notify(msg, 'error')
      }
    },

    async adminAddUser() {
      if (!this.addUserSelected) return
      this.addingUser = true
      try {
        await axios.post(`${this.api}duty_shifts/${this.selectedShift.id}/allocate`, {
          user_id: this.addUserSelected,
        })
        this.notify('Colaborador adicionado ao plantão.', 'success')
        this.addUserSelected = ''
        await this.openAllocationsModal(this.selectedShift)
        this.loadShifts()
      } catch (e) {
        const msg = (e.response && e.response.data && e.response.data.message) || 'Erro ao adicionar colaborador.'
        this.notify(msg, 'error')
      } finally {
        this.addingUser = false
      }
    },

    openRejectModal(alloc) {
      this.rejectTargetAllocation = alloc
      this.rejectReason = ''
      this.showRejectModal = true
    },

    closeRejectModal() {
      this.showRejectModal = false
      this.rejectTargetAllocation = null
      this.rejectReason = ''
    },

    async confirmReject() {
      if (!this.rejectReason.trim()) return
      this.rejectingAllocation = true
      try {
        await axios.post(`${this.api}duty_shift_allocations/${this.rejectTargetAllocation.id}/reject`, {
          rejection_reason: this.rejectReason,
        })
        this.notify('Solicitação rejeitada.', 'success')
        this.closeRejectModal()
        this.openAllocationsModal(this.selectedShift)
      } catch (e) {
        const msg = (e.response && e.response.data && e.response.data.message) || 'Erro ao rejeitar.'
        this.notify(msg, 'error')
      } finally {
        this.rejectingAllocation = false
      }
    },

    async removeAllocation(alloc) {
      const result = await swal({
        title: 'Remover colaborador',
        text: 'Deseja remover este colaborador do plantão?',
        icon: 'warning',
        buttons: { cancel: 'Cancelar', confirm: { text: 'Remover', value: true, className: 'btn-danger' } },
        dangerMode: true,
      })
      if (!result) return
      try {
        await axios.delete(`${this.api}duty_shift_allocations/${alloc.id}`)
        this.notify('Colaborador removido. Vaga disponível novamente.', 'success')
        this.openAllocationsModal(this.selectedShift)
        this.loadShifts()
      } catch (e) {
        this.notify('Erro ao remover alocação.', 'error')
      }
    },

    toggleSelectAll(e) {
      if (e.target.checked) {
        this.selectedAllocIds = this.filteredPendingAllocations.map(a => a.id)
      } else {
        this.selectedAllocIds = []
      }
    },

    async bulkApprove() {
      if (!this.selectedAllocIds.length) return
      this.bulkApproving = true
      const count = this.selectedAllocIds.length
      try {
        await Promise.all(
          this.selectedAllocIds.map(id => axios.post(`${this.api}duty_shift_allocations/${id}/approve`)),
        )
        this.selectedAllocIds = []
        this.notify(`${count} solicitação(ões) aprovada(s).`, 'success')
        this.openAllocationsModal(this.selectedShift)
        this.loadShifts()
      } catch (e) {
        const msg = (e.response && e.response.data && e.response.data.message) || 'Erro ao aprovar.'
        this.notify(msg, 'error')
      } finally {
        this.bulkApproving = false
      }
    },

    openBulkRejectModal() {
      this.bulkRejectReason = ''
      this.showBulkRejectModal = true
    },

    closeBulkRejectModal() {
      this.showBulkRejectModal = false
      this.bulkRejectReason = ''
    },

    async bulkReject() {
      if (!this.bulkRejectReason.trim() || !this.selectedAllocIds.length) return
      this.bulkRejecting = true
      try {
        await Promise.all(
          this.selectedAllocIds.map(id => axios.post(`${this.api}duty_shift_allocations/${id}/reject`, { rejection_reason: this.bulkRejectReason })),
        )
        const count = this.selectedAllocIds.length
        this.selectedAllocIds = []
        this.closeBulkRejectModal()
        this.notify(`${count} solicitação(ões) rejeitada(s).`, 'success')
        this.openAllocationsModal(this.selectedShift)
        this.loadShifts()
      } catch (e) {
        const msg = (e.response && e.response.data && e.response.data.message) || 'Erro ao rejeitar.'
        this.notify(msg, 'error')
      } finally {
        this.bulkRejecting = false
      }
    },

    openImportModal() {
      this.showImportModal = true
      this.importFile = null
      this.importStatus = ''
      this.importProgressPercent = 0
      this.importResults = null
    },

    closeImportModal() {
      this.showImportModal = false
    },

    onFileChange(e) {
      this.importFile = e.target.files[0] || null
    },

    async downloadTemplate() {
      try {
        const res = await axios.get(`${this.api}duty_shifts/import/template`, { responseType: 'blob' })
        const url = window.URL.createObjectURL(new Blob([res.data]))
        const link = document.createElement('a')
        link.href = url
        link.setAttribute('download', 'template_importacao_plantoes.xlsx')
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
      } catch (e) {
        this.notify('Erro ao baixar template.', 'error')
      }
    },

    async startImport() {
      if (!this.importFile) return
      this.importInProgress = true
      this.importStatus = 'Enviando arquivo...'
      this.importProgressPercent = 0
      this.importResults = null

      const formData = new FormData()
      formData.append('file', this.importFile)

      try {
        const res = await axios.post(`${this.api}duty_shifts/import`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
        })
        const jobId = res.data.job_id
        this.importJobId = jobId
        localStorage.setItem('duty_shift_import_job_id', jobId)
        this.importInBackground = true
        this.closeImportModal()
        this.pollImportProgress(jobId)
      } catch (e) {
        const msg = e.response?.data?.message || 'Erro ao importar.'
        this.importStatus = msg
        this.notify(msg, 'error')
      } finally {
        this.importInProgress = false
      }
    },

    pollImportProgress(jobId) {
      this.stopPolling()
      this.importPollingInterval = setInterval(async () => {
        try {
          const res = await axios.get(`${this.api}duty_shifts/import/progress/${jobId}`)
          const data = res.data.data || {}
          this.importStatus = data.message || ''
          this.importProgressPercent = data.percent || 0

          if (data.status === 'completed' || data.status === 'failed') {
            this.stopPolling()
            this.importInBackground = false
            localStorage.removeItem('duty_shift_import_job_id')
            this.handleImportCompletion(jobId)
          }
        } catch (e) {
          this.stopPolling()
        }
      }, 2000)
    },

    async handleImportCompletion(jobId) {
      try {
        const res = await axios.get(`${this.api}duty_shifts/import/results/${jobId}`)
        const reports = res.data.data?.reports || []
        const successList = reports.filter(r => r.status === 'success')
        const errorList = reports.filter(r => r.status === 'error')
        this.importResults = {
          success: successList.length,
          errors: errorList.length,
          errorList,
        }
        this.loadShifts()
        this.showImportModal = true
      } catch (e) {
        this.notify('Erro ao buscar resultados da importação.', 'error')
      }
    },

    checkImportResults() {
      if (this.importJobId) {
        this.handleImportCompletion(this.importJobId)
      }
    },

    stopPolling() {
      if (this.importPollingInterval) {
        clearInterval(this.importPollingInterval)
        this.importPollingInterval = null
      }
    },

    restoreImportState() {
      const savedJobId = localStorage.getItem('duty_shift_import_job_id')
      if (savedJobId) {
        this.importJobId = savedJobId
        this.importInBackground = true
        this.pollImportProgress(savedJobId)
      }
    },

    formatShiftTimeRange(dutyDay, startTime, endTime) {
      if (!dutyDay || !startTime || !endTime) return '—'
      try {
        const extractHM = v => {
          const s = String(v)
          if (s.includes('T')) return s.split('T')[1].slice(0, 5)
          if (s.length > 8 && s.includes(' ')) return s.split(' ')[1].slice(0, 5)
          return s.slice(0, 5)
        }
        const [y, m, d] = String(dutyDay).slice(0, 10).split('-').map(Number)
        const startDate = new Date(y, m - 1, d)
        const pad = n => String(n).padStart(2, '0')
        const fmtDate = dt => `${pad(dt.getDate())}/${pad(dt.getMonth() + 1)}`
        const startHM = extractHM(startTime)
        const endHM = extractHM(endTime)
        const [sh, sm] = startHM.split(':').map(Number)
        const [eh, em] = endHM.split(':').map(Number)
        const startMins = sh * 60 + sm
        const endMins = eh * 60 + em
        const endDate = new Date(startDate)
        if (endMins <= startMins) endDate.setDate(endDate.getDate() + 1)
        return `${fmtDate(startDate)} ${startHM} – ${fmtDate(endDate)} ${endHM}`
      } catch (e) {
        return '—'
      }
    },

    formatDutyDay(value) {
      if (!value) return '—'
      try {
        const str = String(value).slice(0, 10)
        const [y, m, d] = str.split('-').map(Number)
        if (!y || !m || !d) return '—'
        return `${String(d).padStart(2, '0')}/${String(m).padStart(2, '0')}/${y}`
      } catch (e) {
        return '—'
      }
    },

    formatDatetime(value) {
      if (!value) return '—'
      try {
        const d = new Date(value)
        return `${d.toLocaleDateString('pt-BR')} ${d.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}`
      } catch (e) {
        return value
      }
    },

    toInputDatetime(value) {
      if (!value) return ''
      try {
        const d = new Date(value)
        const pad = n => String(n).padStart(2, '0')
        return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`
      } catch (e) {
        return ''
      }
    },

    openReleaseRequestsModal() {
      this.rrPage = 1
      this.rrSearch = ''
      this.showReleaseRequestsModal = true
      this.loadPendingReleaseRequests()
    },

    async loadPendingReleaseRequests() {
      this.loadingReleaseRequests = true
      try {
        const res = await axios.get(`${this.api}work_schedule_release_requests?status=pending`)
        this.pendingReleaseRequests = res.data || []
        this.rrPage = 1
      } catch (e) {
        console.error('Erro ao carregar solicitações de liberação:', e)
      } finally {
        this.loadingReleaseRequests = false
      }
    },

    openApproveReleaseModal(rr) {
      this.approvingReleaseReq = rr
      this.approveReleaseForm = {
        create_plantao: false, plantao_title: '', plantao_vacancies: 1, plantao_requirement_ids: [],
      }
      this.showApproveReleaseModal = true
    },

    async submitApproveRelease() {
      if (!this.approvingReleaseReq) return
      this.submittingApproveRelease = true
      try {
        const shouldCreatePlantao = this.approveReleaseForm.create_plantao
        const res = await axios.post(`${this.api}work_schedule_release_requests/${this.approvingReleaseReq.id}/approve`, {
          create_plantao: shouldCreatePlantao,
          plantao_title: shouldCreatePlantao ? (this.approveReleaseForm.plantao_title || null) : null,
          plantao_vacancies: shouldCreatePlantao ? (this.approveReleaseForm.plantao_vacancies || 1) : null,
          plantao_requirement_ids: shouldCreatePlantao ? this.approveReleaseForm.plantao_requirement_ids : [],
        })
        const createdShift = res.data && res.data.duty_shift
        this.notify(
          createdShift ? 'Solicitação aprovada. Plantão criado com sucesso.' : 'Solicitação aprovada.',
          'success',
        )
        this.showApproveReleaseModal = false
        this.approvingReleaseReq = null
        await this.loadPendingReleaseRequests()
        if (createdShift) {
          this.filterFrom = ''
          this.filterTo = ''
        }
        await this.loadShifts()
      } catch (e) {
        const msg = (e.response && e.response.data && e.response.data.message) || 'Erro ao aprovar.'
        this.notify(msg, 'error')
      } finally {
        this.submittingApproveRelease = false
      }
    },

    openRejectReleaseModal(rr) {
      this.rejectingReleaseReq = rr
      this.rejectReleaseReason = ''
      this.showRejectReleaseModal = true
    },

    async submitRejectRelease() {
      if (!this.rejectingReleaseReq) return
      this.submittingRejectRelease = true
      try {
        await axios.post(`${this.api}work_schedule_release_requests/${this.rejectingReleaseReq.id}/reject`, {
          rejection_reason: this.rejectReleaseReason || null,
        })
        this.notify('Solicitação recusada.', 'success')
        this.showRejectReleaseModal = false
        this.rejectingReleaseReq = null
        await this.loadPendingReleaseRequests()
      } catch (e) {
        const msg = (e.response && e.response.data && e.response.data.message) || 'Erro ao rejeitar.'
        this.notify(msg, 'error')
      } finally {
        this.submittingRejectRelease = false
      }
    },

    calcRemainingDisplay(rr) {
      if (!rr || !rr.work_schedule) return null
      const ws = rr.work_schedule
      const oldStart = ws.shift_start_time
      const oldEnd = ws.shift_end_time
      const newStart = rr.proposed_start_time
      const newEnd = rr.proposed_end_time
      if (!oldStart || !oldEnd || !newStart || !newEnd) return null

      const extractHM = v => {
        const s = String(v)
        if (s.includes('T')) return s.split('T')[1].slice(0, 5)
        if (s.length > 8 && s.includes(' ')) return s.split(' ')[1].slice(0, 5)
        return s.slice(0, 5)
      }
      const toMin = t => { const [h, m] = extractHM(t).split(':'); return +h * 60 + +m }
      const oStartMin = toMin(oldStart)
      let oEnd = toMin(oldEnd); if (oEnd <= oStartMin) oEnd += 1440
      const nStartMin = toMin(newStart)
      let nEnd = toMin(newEnd); if (nEnd <= nStartMin) nEnd += 1440

      if (nEnd >= oEnd) return null
      const remStartMins = nEnd % 1440
      const remEndMins = oEnd % 1440
      const fmt = m => `${String(Math.floor(m / 60) % 24).padStart(2, '0')}:${String(m % 60).padStart(2, '0')}`

      const dutyDay = ws.duty_day
      if (dutyDay) {
        const [y, mo, d] = String(dutyDay).slice(0, 10).split('-').map(Number)
        const pad = n => String(n).padStart(2, '0')
        const addDays = (base, days) => {
          const dt = new Date(base.getTime())
          dt.setDate(dt.getDate() + days)
          return dt
        }
        const baseDate = new Date(y, mo - 1, d)
        const remStartDate = addDays(baseDate, nEnd >= 1440 ? 1 : 0)
        const remEndDate = addDays(baseDate, oEnd >= 1440 ? 1 : 0)
        const fmtDate = dt => `${pad(dt.getDate())}/${pad(dt.getMonth() + 1)}`
        return `${fmtDate(remStartDate)} ${fmt(remStartMins)} – ${fmtDate(remEndDate)} ${fmt(remEndMins)}`
      }

      return `${fmt(remStartMins)} – ${fmt(remEndMins)}`
    },

    notify(msg, type) {
      if (typeof swal !== 'undefined') {
        const iconMap = {
          success: 'success', error: 'error', warning: 'warning', info: 'info',
        }
        swal(type === 'error' ? 'Erro' : (type === 'warning' ? 'Atenção' : 'Sucesso'), msg, iconMap[type] || 'info')
      } else {
        alert(msg)
      }
    },
  },
}
</script>

<style scoped>
.modal-backdrop-custom {
  position: fixed;
  top: 0; left: 0; right: 0; bottom: 0;
  background: rgba(0,0,0,0.5);
  z-index: 1050;
  display: flex;
  align-items: flex-start;
  justify-content: center;
  overflow-y: auto;
  padding: 0 12px 40px;
}

/* Modais maiores e mais legíveis */
.modal-backdrop-custom .modal-content {
  border: none;
  border-radius: 12px;
  box-shadow: 0 18px 48px rgba(16, 24, 40, 0.22);
  overflow: hidden;
}
.modal-backdrop-custom .modal-header {
  padding: 18px 24px;
  background: #f8f9fb;
  border-bottom: 1px solid #e9ecef;
}
.modal-backdrop-custom .modal-header .modal-title {
  font-size: 1.18rem;
  font-weight: 600;
}
.modal-backdrop-custom .modal-body {
  padding: 24px;
}
.modal-backdrop-custom .modal-footer {
  padding: 16px 24px;
  background: #f8f9fb;
  border-top: 1px solid #e9ecef;
}
</style>
